import React, { useCallback, useState, useEffect } from "react";
import {
	Box,
	Grid2,
	Typography,
	Avatar,
	TableContainer,
	Paper,
	Table,
	TableBody,
	TableRow,
	TableCell,
	TableHead,
	Button,
	TextField,
	CircularProgress,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import logo from "../Malayan.png";
import { getInvoice, updateAssignmentStatus } from "../utility/api";
import { setAssignment } from "../features/AssignmentSlice";

// Standard fee schedule, used only until the adjuster edits the fee.  Mirrors
// DefaultServiceFee in PDFFunction.cs: exactly 5,000 - and an unknown settled amount -
// attract the lower fee, so the figure on screen matches the one on the invoice.
const defaultServiceFee = (settledAmount) => {
	const amount = Number(settledAmount);
	return Number.isFinite(amount) && amount > 5000 ? 750 : 550;
};

const MACStandardFeeNote = ({ onCloseInvoice }) => {
	const [serviceFee, setServiceFee] = useState(0);
	const [serviceFeeEdited, setServiceFeeEdited] = useState(false);
	const [serviceTax, setServiceTax] = useState(0);
	const [total, setTotal] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [additionalRows, setAdditionalRows] = useState([
		{ description: "", amount: "" },
		{ description: "", amount: "" },
		{ description: "", amount: "" },
	]);

	const dispatch = useDispatch();

	const currentAssignment = useSelector(
		(state) => state.assignment.assignment,
	);
	const user = useSelector((state) => state.user.user);

	// The single fee the whole component works from: what the adjuster typed, or the
	// schedule default until they do.  Submission sends this same number, so the invoice
	// cannot disagree with what is on screen.
	const effectiveServiceFee = serviceFeeEdited
		? Number(serviceFee) || 0
		: defaultServiceFee(currentAssignment?.settledAmount);

	const calculateServiceCharges = useCallback(() => {
		const baseFee = effectiveServiceFee;
		const tax = 0.08 * baseFee;

		const additionalValuesTotal = additionalRows.reduce(
			(sum, row) => sum + Number(row.amount || 0),
			0,
		);

		// Only set default when user hasn't edited
		if (!serviceFeeEdited) {
			setServiceFee(baseFee.toFixed(2));
		}

		setServiceTax(tax.toFixed(2));
		setTotal((baseFee + tax + additionalValuesTotal).toFixed(2));
	}, [additionalRows, effectiveServiceFee, serviceFeeEdited]);

	useEffect(() => {
		calculateServiceCharges();
	}, [calculateServiceCharges]);

	const handleAdditionalRowChange = (index, field, value) => {
		const updatedRows = [...additionalRows];
		updatedRows[index][field] = value;
		setAdditionalRows(updatedRows);
	};

	const uploadInvoice = async () => {
		try {
			const cleanedData = additionalRows
				.filter(
					(item) =>
						item.description && item.description.trim() !== "",
				)
				.map((item) => ({
					description: item.description,
					amount: parseFloat(item.amount) || 0,
				}));

			// The fee has to travel with the request: without it the backend fell back to
			// the schedule and silently replaced whatever was typed here.
			const response = await getInvoice(
				cleanedData,
				currentAssignment.assignmentName,
				effectiveServiceFee,
			);
			console.log(response.status);

			const updatedAssignment = {
				...currentAssignment,
				status: "Completed",
			};

			const updatedAssignmentList = await updateAssignmentStatus(
				currentAssignment.assignmentName,
				"Completed",
				user,
			);
			dispatch(setAssignment(updatedAssignment));
			alert("Invoice uploaded successfully");
			if (onCloseInvoice) {
				onCloseInvoice(updatedAssignmentList);
			}
		} catch (error) {
			console.error("Error uploading PDF:", error);
			alert("An error occurred while uploading the PDF");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleSubmit = () => {
		setIsSubmitting(true);
		uploadInvoice();
	};

	return (
		<Box sx={{ padding: 4, backgroundColor: "#f9f9f9", borderRadius: 2 }}>
			<Box
				id="pdfContent"
				sx={{
					fontFamily: "Arial, sans-serif",
					padding: 4,
					backgroundColor: "#fff",
					borderRadius: 2,
				}}
			>
				{/* Header and Address Section */}
				<Grid2
					container
					spacing={4}
					alignItems="center"
					justifyContent="space-between"
					sx={{ mb: 4 }}
				>
					<Grid2 item xs={12} md={8} textAlign="left">
						<Typography variant="h6">
							MALAYAN ADJUSTMENT COMPANY SDN BHD (3616-W)
						</Typography>
						<Typography>
							Unit A-5-1, Block A, Megan Avenue 1
						</Typography>
						<Typography>189, Jln Tun Razak</Typography>
						<Typography>50400 Kuala Lumpur, Malaysia</Typography>
						<Typography>
							T: 03-2166 6633 | F: 03-2163 3636
						</Typography>
						<Typography>
							@ :adjusters@macsb.com.my | W : www.macsb.com.my
						</Typography>
					</Grid2>
					<Grid2 item xs={12} md={4} textAlign="center">
						<Avatar
							alt="Company Logo"
							src={logo}
							sx={{ width: 150, height: 150, mx: "auto" }}
						/>
					</Grid2>
				</Grid2>

				{/* Table Section */}
				<TableContainer component={Paper} sx={{ mb: 4 }}>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>
									<strong>No.</strong>
								</TableCell>
								<TableCell>
									<strong>Description</strong>
								</TableCell>
								<TableCell>
									<strong>Total(RM)</strong>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							<TableRow>
								<TableCell>1.</TableCell>
								<TableCell>Service Fee</TableCell>
								<TableCell>
									<TextField
										value={serviceFee}
										placeholder="Service Fee"
										onChange={(e) => {
											setServiceFeeEdited(true);
											setServiceFee(e.target.value);
										}}
										size="small"
										type="number"
										slotProps={{
											input: {
												step: "0.01",
												min: 0,
											},
										}}
										fullWidth
										sx={{ marginBottom: 1 }}
									/>
								</TableCell>
							</TableRow>
							{additionalRows.map((row, index) => (
								<TableRow key={index}>
									<TableCell>{2 + index}.</TableCell>
									<TableCell>
										<TextField
											value={row.description}
											placeholder="Description"
											onChange={(e) =>
												handleAdditionalRowChange(
													index,
													"description",
													e.target.value,
												)
											}
											size="small"
											fullWidth
											sx={{ marginBottom: 1 }}
										/>
									</TableCell>
									<TableCell>
										<TextField
											value={row.amount}
											placeholder="Amount"
											onChange={(e) =>
												handleAdditionalRowChange(
													index,
													"amount",
													e.target.value,
												)
											}
											size="small"
											type="number"
											slotProps={{
												input: {
													step: "0.01",
													min: 0,
													inputMode: "decimal",
												},
											}}
											fullWidth
											sx={{ marginBottom: 1 }}
										/>
									</TableCell>
								</TableRow>
							))}
							<TableRow>
								<TableCell></TableCell>
								<TableCell>Service Tax 8%</TableCell>
								<TableCell>{serviceTax}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell></TableCell>
								<TableCell>
									<strong>Total</strong>
								</TableCell>
								<TableCell>{total}</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>

				<Box sx={{ mt: 4 }}>
					<Typography>
						For payment by Bank transfer/ GIRO, please use the
						following details:
					</Typography>
					<Typography>Bank: Maybank Berhad</Typography>
					<Typography>Account No: 514012042695</Typography>
					<Typography>
						Account Name: Malayan Adjustment Company Sdn Bhd
					</Typography>
				</Box>
			</Box>

			<Button
				type="submit"
				variant="contained"
				color="primary"
				sx={{ mt: 2 }}
				disabled={isSubmitting}
				onClick={handleSubmit}
				startIcon={isSubmitting && <CircularProgress size={20} />}
			>
				Submit
			</Button>
		</Box>
	);
};

export default MACStandardFeeNote;
